import React, { useState, useEffect, useCallback } from 'react';
import '@/features/dashboard/Dashboard.css';
import {
    Container,
    Row,
    Col,
    Card,
    Badge,
    Button,
    Spinner,
    Alert,
    ListGroup,
} from 'react-bootstrap';
import GuideLink from '@/components/common/GuideLink';
import ApplicationDeadlineCard from '@/components/common/ApplicationDeadlineCard';
import AccessibleProgressBar from '@/components/common/AccessibleProgressBar';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTerm } from '@/shared/contexts/TermContext';
import { useAuth } from '@/shared/contexts/AuthContext';
import { resolveRoles } from '@/shared/utils/previewRoles';
import { useFeature } from '@/shared/contexts/FeatureFlagContext';
import {
    applyEntryHref,
    canOpenPositions,
} from '@/shared/utils/positionsAccess';
import dashboardService from '@/shared/services/dashboardService';
import userService from '@/shared/services/userService';
import OnboardingTour, {
    getTourSteps,
} from '@/components/common/OnboardingTour';
import ProfileCompletionCard from '@/features/dashboard/components/ProfileCompletionCard';
import FirstTimeHero from '@/features/dashboard/components/FirstTimeHero';
import TodosCard from '@/features/dashboard/components/TodosCard';
import DraftApplicationsCard from '@/features/dashboard/components/DraftApplicationsCard';
import RecentApplicationsCard from '@/features/dashboard/components/RecentApplicationsCard';
import LaApplicantsCard from '@/components/common/LaApplicantsCard';
import { StudentApplicationsNotOpen } from '@/components/common/ProgramPageLinks';
import { studentWindowFor } from '@/shared/utils/studentWindow';
import { badgeTextFor } from '@/shared/utils/badgeText';
import StatusPill from '@/components/common/StatusPill';
import { TeamEmailLink } from '@/components/common/TeamEmailLink';
import MyNominationsCard from '@/features/nominations/MyNominationsCard';

const PROGRAM_CONFIG = {
    si: {
        bg: 'success',
        label: 'SI',
        full: 'Supplemental Instruction',
        color: '#2E7D32',
    },
    ta: {
        bg: 'primary',
        label: 'TA',
        full: 'Teaching Assistant',
        color: '#1565C0',
    },
    la: {
        bg: '',
        label: 'LA',
        full: 'Learning Assistant',
        color: 'var(--psh-la-color)',
        badgeClass: 'badge-la',
    },
};

// Profiles carry one free-text `name`, so titles arrive inline as part of it
// ("Dr. Sarah Johnson"). Skipping them matters for the greeting specifically:
// taking the leading word addresses every titled user by their title instead
// of their name, which reads as broken rather than formal.
const HONORIFICS = new Set([
    'dr',
    'prof',
    'professor',
    'mr',
    'mrs',
    'ms',
    'mx',
]);

/**
 * The name to greet someone by: their given name, with any leading titles
 * skipped. Returns null when nothing usable remains, so the caller supplies
 * the fallback.
 */
const getGivenName = (fullName) =>
    String(fullName || '')
        .trim()
        .split(/\s+/)
        .filter(Boolean)
        // find() rather than a single leading check, so stacked titles
        // ("Prof. Dr. Hans Weber") resolve to the name as well.
        .find(
            (part) => !HONORIFICS.has(part.replace(/\.$/, '').toLowerCase())
        ) || null;

function Dashboard() {
    const { currentTerm, currentTermCode, termsLoading } = useTerm();
    const { userProfile, effectiveRoles, refreshProfile } = useAuth();
    const roles = resolveRoles({ effectiveRoles, userProfile });
    // The coordinator program cards summarise dashboard data and stay; only
    // their "Manage program" link leads into a program dashboard, which is
    // behind coordinatorReview, so the link is too.
    const coordinatorReviewEnabled = useFeature('coordinatorReview');
    // Surveys and training are off for launch (#391). Every card and link on
    // this page that leads into either goes with its flag.
    const surveysEnabled = useFeature('surveys');
    const trainingEnabled = useFeature('training');
    // A student with no applications is invited to apply only while the
    // student application workflow is on.
    const studentApplicationEnabled = useFeature('studentApplication');
    // With hiring on, a student picks positions first, so the apply entry
    // points lead to Open Positions for accounts its route admits. That
    // route needs the student application workflow too, so both flags.
    const hiringEnabled = useFeature('hiring');
    const applyHref = applyEntryHref({
        hiring: hiringEnabled,
        studentApplication: studentApplicationEnabled,
        roles,
        fallback: '/courses',
    });
    const quickApplyToPositions = applyHref === '/positions';
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [dashboardData, setDashboardData] = useState(null);
    const [showTour, setShowTour] = useState(false);
    const [tourInitialStep, setTourInitialStep] = useState(0);

    const isCoordinator = roles.some((role) =>
        [
            'si_coordinator',
            'ta_coordinator',
            'la_coordinator',
            'admin',
        ].includes(role)
    );

    const isFaculty = roles.includes('faculty');
    const isStudent = roles.includes('student');
    // Faculty access is granted at sign-up only when the directory
    // affiliation proves it, and the account type is derived from that, never
    // chosen, so faculty and staff it missed are indistinguishable from
    // students here. A student ID does not separate them: it is optional, and
    // a faculty or staff member who is also a student may have given one.
    // Every account without faculty, coordinator or admin access is told how
    // to get it; a student seeing the note is the lesser harm.
    const needsFacultyAccess =
        Boolean(userProfile) && !isFaculty && !isCoordinator;
    // Same audience as the Request Support route and its menu entry, so the
    // button never offers a page the route guard would turn away.
    const canRequestSupport = roles.some((role) =>
        ['faculty', 'admin'].includes(role)
    );

    // Determine if the tour should auto-show for first-time users
    useEffect(() => {
        if (userProfile && !userProfile.hasSeenTour) {
            const savedStep = userProfile.tourStep || 0;
            const stepCount = getTourSteps(userProfile.roles || [], {
                training: trainingEnabled,
                studentApplication: studentApplicationEnabled,
                coordinatorReview: coordinatorReviewEnabled,
            }).length;
            const validStep = savedStep < stepCount ? savedStep : 0;
            setTourInitialStep(validStep);
            setShowTour(true);
        }
    }, [
        userProfile,
        trainingEnabled,
        studentApplicationEnabled,
        coordinatorReviewEnabled,
    ]);

    const handleTourComplete = useCallback(() => {
        setShowTour(false);
        // Refresh profile to update profileCompletion
        if (refreshProfile) {
            refreshProfile();
        }
    }, [refreshProfile]);

    const location = useLocation();
    const navigate = useNavigate();

    const handleStartTour = useCallback(async () => {
        try {
            await userService.resetTour();
        } catch (_err) {
            // resetTour failure is non-fatal; tour starts from step 0 regardless
        }
        setTourInitialStep(0);
        setShowTour(true);
    }, []);

    // The user menu's "Restart tour" arrives here with this state. It is
    // cleared at once so a reload or Back does not start the tour again.
    useEffect(() => {
        if (!location.state?.restartTour) return;
        navigate(location.pathname, { replace: true, state: null });
        handleStartTour();
    }, [location, navigate, handleStartTour]);

    useEffect(() => {
        const loadDashboardData = async () => {
            try {
                setLoading(true);
                setError(null);

                setDashboardData(await dashboardService.getDashboard());
            } catch (_err) {
                setError('Failed to load dashboard data. Please try again.');
            } finally {
                setLoading(false);
            }
        };

        loadDashboardData();
    }, []);

    const getGreeting = () => {
        const hour = new Date().getHours();
        if (hour < 12) return 'Good morning';
        if (hour < 17) return 'Good afternoon';
        return 'Good evening';
    };

    // Wait for the term too, so the Edit buttons don't pop in late
    if (loading || termsLoading) {
        return (
            <Container className="py-5 text-center">
                <Spinner animation="border" role="status" variant="primary">
                    <span className="visually-hidden">Loading...</span>
                </Spinner>
                <p className="mt-3 text-muted">Loading dashboard...</p>
            </Container>
        );
    }

    if (error) {
        return (
            <Container className="py-5">
                <Alert variant="danger">
                    <Alert.Heading>Error Loading Dashboard</Alert.Heading>
                    <p>{error}</p>
                    <Button
                        variant="outline-danger"
                        onClick={() => window.location.reload()}
                    >
                        Try Again
                    </Button>
                </Alert>
            </Container>
        );
    }

    const studentData = dashboardData?.student;
    const facultyData = dashboardData?.faculty;
    const coordinatorData = dashboardData?.coordinator;
    // A student with no application yet is led to the one thing to do. It
    // needs the positions route, and an open LA window to point at.
    const showFirstTimeHero =
        isStudent &&
        studentData?.applications?.length === 0 &&
        studentApplicationEnabled &&
        hiringEnabled &&
        canOpenPositions(roles) &&
        studentWindowFor(currentTerm, 'la').accepting;

    return (
        <Container className="py-4">
            {/* Onboarding Tour Modal */}
            <OnboardingTour
                show={showTour}
                onComplete={handleTourComplete}
                initialStep={tourInitialStep}
                userRoles={userProfile.roles || []}
            />

            {/* Welcome Header */}
            <div className="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-4">
                <div>
                    <h1>
                        {getGreeting()},{' '}
                        {getGivenName(userProfile?.name) || 'there'}!
                    </h1>
                    <p className="text-muted mb-1">
                        {isCoordinator
                            ? coordinatorReviewEnabled
                                ? 'Manage your programs and review applications'
                                : 'Your programs at a glance'
                            : isFaculty
                              ? 'Request peer support for your courses'
                              : 'Track your applications and assignments'}
                    </p>
                    {(isFaculty || (isStudent && !showFirstTimeHero)) && (
                        <GuideLink
                            audience={isFaculty ? 'faculty' : 'student'}
                            className="small"
                            short
                        />
                    )}
                </div>
                {/* Requesting support is what faculty come here to do,
                    so it is offered where they land rather than only in
                    the menu. */}
                {canRequestSupport && (
                    <Button
                        as={Link}
                        to="/applications/start"
                        variant="primary"
                    >
                        Request support
                    </Button>
                )}
            </div>

            {showFirstTimeHero && (
                <FirstTimeHero
                    term={currentTerm}
                    draft={
                        studentData.draftApplication?.term === currentTerm?.code
                            ? studentData.draftApplication
                            : null
                    }
                />
            )}

            {(isStudent || isFaculty) && <TodosCard />}

            {isFaculty && (
                <ApplicationDeadlineCard
                    term={currentTerm}
                    facultyEmail={userProfile?.email}
                />
            )}

            {canRequestSupport && <DraftApplicationsCard />}
            {canRequestSupport && <RecentApplicationsCard />}

            {/* Profile Completion Card (shown for non-student roles above main content) */}
            {(isCoordinator || isFaculty) && userProfile?.profileCompletion && (
                <div className="mb-4">
                    <ProfileCompletionCard
                        profileCompletion={userProfile.profileCompletion}
                        onStartTour={handleStartTour}
                    />
                </div>
            )}

            {/* Coordinator Quick Links */}
            {isCoordinator && (
                <section className="mb-4">
                    <Row>
                        <Col md={6} className="mb-3">
                            <Card
                                as={Link}
                                to="/analytics"
                                className="h-100 text-decoration-none border-0 shadow-sm hover-lift"
                            >
                                <Card.Body className="d-flex align-items-center gap-3 py-3">
                                    <div className="display-6">📊</div>
                                    <div>
                                        <h2 className="h6 mb-1">
                                            Analytics Dashboard
                                        </h2>
                                        <small className="text-muted">
                                            Coverage, outcomes, trends, and
                                            distribution data
                                        </small>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                        {surveysEnabled && (
                            <Col md={6} className="mb-3">
                                <Card
                                    as={Link}
                                    to="/surveys"
                                    className="h-100 text-decoration-none border-0 shadow-sm hover-lift"
                                >
                                    <Card.Body className="d-flex align-items-center gap-3 py-3">
                                        <div className="display-6">📋</div>
                                        <div>
                                            <h2 className="h6 mb-1">
                                                Surveys &amp; Evaluations
                                            </h2>
                                            <small className="text-muted">
                                                Deploy surveys, view results,
                                                and track feedback
                                            </small>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        )}
                    </Row>
                </section>
            )}

            {/* Coordinator Section */}
            {isCoordinator && coordinatorData?.programs?.length > 0 && (
                <section className="mb-5">
                    <h2 className="h4 mb-3">Program Management</h2>
                    <Row>
                        {coordinatorData.programs.map((program) => (
                            <Col lg={4} className="mb-4" key={program.type}>
                                <Card
                                    className="h-100 border-0 shadow-sm"
                                    style={{
                                        borderTop: `4px solid ${program.color}`,
                                    }}
                                >
                                    <Card.Body>
                                        <div className="d-flex justify-content-between align-items-start mb-3">
                                            <div>
                                                <Badge
                                                    bg={
                                                        PROGRAM_CONFIG[
                                                            program.type
                                                        ]?.bg
                                                    }
                                                    text={badgeTextFor(
                                                        PROGRAM_CONFIG[
                                                            program.type
                                                        ]?.bg
                                                    )}
                                                    className={`mb-2 ${PROGRAM_CONFIG[program.type]?.badgeClass || ''}`}
                                                >
                                                    {program.type.toUpperCase()}{' '}
                                                    Program
                                                </Badge>
                                                <h3 className="h5 mb-0">
                                                    {program.name}
                                                </h3>
                                            </div>
                                        </div>
                                        <Row className="text-center mb-3">
                                            {program.type === 'ta' ? (
                                                <>
                                                    <Col>
                                                        <p className="h3 mb-0 text-warning">
                                                            {program.stats
                                                                .pendingRequests ||
                                                                0}
                                                        </p>
                                                        <small className="text-muted">
                                                            Pending
                                                        </small>
                                                    </Col>
                                                    <Col>
                                                        <p className="h3 mb-0 text-success">
                                                            {program.stats
                                                                .activeTAs || 0}
                                                        </p>
                                                        <small className="text-muted">
                                                            Active
                                                        </small>
                                                    </Col>
                                                    <Col>
                                                        <p className="h3 mb-0 text-info">
                                                            {program.stats
                                                                .pendingHR || 0}
                                                        </p>
                                                        <small className="text-muted">
                                                            HR Pending
                                                        </small>
                                                    </Col>
                                                </>
                                            ) : (
                                                <>
                                                    <Col>
                                                        <p className="h3 mb-0 text-warning">
                                                            {program.stats
                                                                .pendingApplications ||
                                                                0}
                                                        </p>
                                                        <small className="text-muted">
                                                            Pending
                                                        </small>
                                                    </Col>
                                                    <Col>
                                                        <p className="h3 mb-0 text-success">
                                                            {program.stats
                                                                .activeLeaders ||
                                                                program.stats
                                                                    .activeLAs ||
                                                                0}
                                                        </p>
                                                        <small className="text-muted">
                                                            Active
                                                        </small>
                                                    </Col>
                                                </>
                                            )}
                                        </Row>
                                        {coordinatorReviewEnabled && (
                                            <Button
                                                as={Link}
                                                to={`/programs/${program.type}`}
                                                variant={
                                                    program.type === 'la'
                                                        ? 'outline-la'
                                                        : `outline-${PROGRAM_CONFIG[program.type]?.bg}`
                                                }
                                                className="w-100"
                                            >
                                                Manage{' '}
                                                {program.type.toUpperCase()}{' '}
                                                Program
                                            </Button>
                                        )}
                                    </Card.Body>
                                </Card>
                            </Col>
                        ))}
                    </Row>
                </section>
            )}

            {/* Faculty Section */}
            {isFaculty && facultyData && (
                <section className="mb-5">
                    {facultyData.taRequests?.length > 0 && (
                        <Card className="border-0 shadow-sm mb-4">
                            <Card.Header className="bg-transparent border-0">
                                <h2 className="h5 mb-0">Your TA Requests</h2>
                            </Card.Header>
                            <Card.Body>
                                <ListGroup variant="flush">
                                    {facultyData.taRequests
                                        .slice(0, 3)
                                        .map((request) => (
                                            <ListGroup.Item
                                                key={request.id}
                                                className="px-0"
                                            >
                                                <div className="d-flex justify-content-between align-items-center">
                                                    <div>
                                                        <strong>
                                                            {request.course}
                                                        </strong>
                                                        <span className="text-muted ms-2">
                                                            -{' '}
                                                            {
                                                                request.namedStudent
                                                            }
                                                        </span>
                                                    </div>
                                                    <StatusPill
                                                        family="taRequest"
                                                        value={request.status}
                                                    />
                                                </div>
                                                <small className="text-muted">
                                                    {request.requestedHours}{' '}
                                                    hrs/week
                                                </small>
                                                {request.status === 'denied' &&
                                                    request.reviewNotes && (
                                                        <div className="small text-muted text-break mt-1">
                                                            Reason:{' '}
                                                            {
                                                                request.reviewNotes
                                                            }
                                                        </div>
                                                    )}
                                            </ListGroup.Item>
                                        ))}
                                </ListGroup>
                            </Card.Body>
                        </Card>
                    )}

                    {facultyData.supportedCourses?.length > 0 && (
                        <Card className="border-0 shadow-sm">
                            <Card.Header className="bg-transparent border-0">
                                <h2 className="h5 mb-0">
                                    Courses with Peer Support
                                </h2>
                            </Card.Header>
                            <Card.Body>
                                <ListGroup variant="flush">
                                    {facultyData.supportedCourses.map(
                                        (course) => (
                                            <ListGroup.Item
                                                key={course.assignmentId}
                                                className="px-0"
                                            >
                                                <div className="d-flex justify-content-between align-items-center">
                                                    <div>
                                                        <Badge
                                                            bg={
                                                                PROGRAM_CONFIG[
                                                                    course
                                                                        .programType
                                                                ]?.bg
                                                            }
                                                            text={badgeTextFor(
                                                                PROGRAM_CONFIG[
                                                                    course
                                                                        .programType
                                                                ]?.bg
                                                            )}
                                                            className={`me-2 ${PROGRAM_CONFIG[course.programType]?.badgeClass || ''}`}
                                                        >
                                                            {
                                                                PROGRAM_CONFIG[
                                                                    course
                                                                        .programType
                                                                ]?.label
                                                            }
                                                        </Badge>
                                                        <strong>
                                                            {course.course}
                                                        </strong>
                                                        <span className="text-muted ms-2">
                                                            -{' '}
                                                            {course.courseTitle}
                                                        </span>
                                                    </div>
                                                    <span className="text-muted">
                                                        {course.peerSupporter}
                                                    </span>
                                                </div>
                                            </ListGroup.Item>
                                        )
                                    )}
                                </ListGroup>
                            </Card.Body>
                        </Card>
                    )}
                    <LaApplicantsCard term={currentTermCode} />
                </section>
            )}

            {/* Student Dashboard */}
            {studentData && (
                <Row>
                    {/* Main Content */}
                    <Col lg={8}>
                        <MyNominationsCard />
                        {/* Application Status */}
                        {!showFirstTimeHero && (
                            <section className="mb-4">
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <h2 className="h4 mb-0">Applications</h2>
                                    <Button
                                        as={Link}
                                        to="/applications"
                                        variant="link"
                                        className="p-0"
                                    >
                                        View all
                                    </Button>
                                </div>
                                {studentData.applications?.length === 0 ? (
                                    <Card className="border-0 bg-light">
                                        {studentApplicationEnabled ? (
                                            <Card.Body className="text-center py-4">
                                                <p className="text-muted mb-3">
                                                    You haven&apos;t submitted
                                                    any applications yet.
                                                </p>
                                                <Button
                                                    as={Link}
                                                    to={
                                                        hiringEnabled &&
                                                        canOpenPositions(roles)
                                                            ? '/positions'
                                                            : '/apply'
                                                    }
                                                    variant="primary"
                                                >
                                                    Apply to Be a Peer Supporter
                                                </Button>
                                            </Card.Body>
                                        ) : (
                                            <Card.Body className="py-4">
                                                <StudentApplicationsNotOpen />
                                            </Card.Body>
                                        )}
                                    </Card>
                                ) : (
                                    studentData.applications?.map((app) => {
                                        const program =
                                            PROGRAM_CONFIG[app.program];
                                        return (
                                            <Card
                                                key={app.id}
                                                className="mb-3 border-0 shadow-sm"
                                            >
                                                <Card.Body>
                                                    <Row className="align-items-center">
                                                        <Col>
                                                            <Badge
                                                                bg={program?.bg}
                                                                text={badgeTextFor(
                                                                    program?.bg
                                                                )}
                                                                className={`me-2 ${program?.badgeClass || ''}`}
                                                            >
                                                                {program?.label ||
                                                                    app.program?.toUpperCase()}
                                                            </Badge>
                                                            <span className="fw-medium">
                                                                {program?.full ||
                                                                    'Peer Supporter'}{' '}
                                                                Application
                                                            </span>
                                                            <div className="small text-muted mt-1">
                                                                {app.termName}
                                                            </div>
                                                        </Col>
                                                        <Col xs="auto">
                                                            <StatusPill
                                                                family="studentApplication"
                                                                value={
                                                                    app.status
                                                                }
                                                            />
                                                        </Col>
                                                        {/* Same rule as the Applications page */}
                                                        {studentApplicationEnabled &&
                                                            app.term ===
                                                                currentTermCode &&
                                                            app.status ===
                                                                'submitted' && (
                                                                <Col xs="auto">
                                                                    <Button
                                                                        as={
                                                                            Link
                                                                        }
                                                                        to={`/apply/${app.id}/edit`}
                                                                        variant="outline-primary"
                                                                        size="sm"
                                                                    >
                                                                        Edit
                                                                    </Button>
                                                                </Col>
                                                            )}
                                                    </Row>
                                                </Card.Body>
                                            </Card>
                                        );
                                    })
                                )}
                            </section>
                        )}

                        {/* Active Assignments */}
                        <section className="mb-4">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h2 className="h4 mb-0">Active Assignments</h2>
                                <Button
                                    as={Link}
                                    to="/assignments"
                                    variant="link"
                                    className="p-0"
                                >
                                    View all
                                </Button>
                            </div>
                            {studentData.assignments?.length === 0 ? (
                                <Card className="border-0 bg-light">
                                    <Card.Body className="text-center py-4">
                                        <p className="text-muted mb-0">
                                            No active assignments at this time.
                                        </p>
                                    </Card.Body>
                                </Card>
                            ) : (
                                studentData.assignments?.map((assignment) => (
                                    <Card
                                        key={assignment.id}
                                        className="mb-3 border-0 shadow-sm"
                                    >
                                        <Card.Body>
                                            <Row className="align-items-center">
                                                <Col>
                                                    <div className="d-flex align-items-center gap-2">
                                                        <Badge
                                                            bg={
                                                                PROGRAM_CONFIG[
                                                                    assignment
                                                                        .programType
                                                                ]?.bg
                                                            }
                                                            text={badgeTextFor(
                                                                PROGRAM_CONFIG[
                                                                    assignment
                                                                        .programType
                                                                ]?.bg
                                                            )}
                                                            className={
                                                                PROGRAM_CONFIG[
                                                                    assignment
                                                                        .programType
                                                                ]?.badgeClass ||
                                                                ''
                                                            }
                                                        >
                                                            {
                                                                PROGRAM_CONFIG[
                                                                    assignment
                                                                        .programType
                                                                ]?.label
                                                            }
                                                        </Badge>
                                                        <span className="fw-medium">
                                                            {assignment.course}
                                                        </span>
                                                        <span className="text-muted">
                                                            -{' '}
                                                            {
                                                                assignment.courseTitle
                                                            }
                                                        </span>
                                                    </div>
                                                </Col>
                                                <Col xs="auto">
                                                    <Badge bg="success">
                                                        Active
                                                    </Badge>
                                                </Col>
                                            </Row>
                                        </Card.Body>
                                    </Card>
                                ))
                            )}
                        </section>

                        {/* Quick Actions */}
                        <section>
                            <h2 className="h4 mb-3">Quick Actions</h2>
                            <Row>
                                <Col sm={6} md={4} className="mb-3">
                                    <Card
                                        as={Link}
                                        to={applyHref}
                                        className="h-100 text-decoration-none border-0 shadow-sm hover-lift"
                                    >
                                        <Card.Body className="text-center py-4">
                                            <div className="display-6 mb-2">
                                                📚
                                            </div>
                                            <Card.Title className="h6">
                                                {quickApplyToPositions
                                                    ? 'Positions'
                                                    : 'Browse Courses'}
                                            </Card.Title>
                                            <small className="text-muted">
                                                {quickApplyToPositions
                                                    ? 'Find positions seeking support'
                                                    : 'Find courses seeking support'}
                                            </small>
                                        </Card.Body>
                                    </Card>
                                </Col>
                                {trainingEnabled && (
                                    <Col sm={6} md={4} className="mb-3">
                                        <Card
                                            as={Link}
                                            to="/training"
                                            className="h-100 text-decoration-none border-0 shadow-sm hover-lift"
                                        >
                                            <Card.Body className="text-center py-4">
                                                <div className="display-6 mb-2">
                                                    🎓
                                                </div>
                                                <Card.Title className="h6">
                                                    Training
                                                </Card.Title>
                                                <small className="text-muted">
                                                    Complete required modules
                                                </small>
                                            </Card.Body>
                                        </Card>
                                    </Col>
                                )}
                                <Col sm={6} md={4} className="mb-3">
                                    <Card
                                        as={Link}
                                        to="/profile"
                                        className="h-100 text-decoration-none border-0 shadow-sm hover-lift"
                                    >
                                        <Card.Body className="text-center py-4">
                                            <div className="display-6 mb-2">
                                                👤
                                            </div>
                                            <Card.Title className="h6">
                                                My Profile
                                            </Card.Title>
                                            <small className="text-muted">
                                                Update your information
                                            </small>
                                        </Card.Body>
                                    </Card>
                                </Col>
                            </Row>
                        </section>
                    </Col>

                    {/* Sidebar */}
                    <Col lg={4}>
                        {/* Profile Completion */}
                        {userProfile?.profileCompletion && (
                            <div className="mb-4">
                                <ProfileCompletionCard
                                    profileCompletion={
                                        userProfile.profileCompletion
                                    }
                                    onStartTour={handleStartTour}
                                />
                            </div>
                        )}

                        {/* Training Progress */}
                        {trainingEnabled && (
                            <Card className="mb-4 border-0 shadow-sm">
                                <Card.Header className="bg-transparent border-0 pb-0">
                                    <div className="d-flex justify-content-between align-items-center">
                                        <h2 className="h6 mb-0">
                                            Training Progress
                                        </h2>
                                        <Link to="/training" className="small">
                                            View all
                                        </Link>
                                    </div>
                                </Card.Header>
                                <Card.Body>
                                    {studentData.trainingProgress ? (
                                        <>
                                            <div className="mb-2">
                                                <div className="d-flex justify-content-between small mb-1">
                                                    <span>
                                                        {
                                                            studentData
                                                                .trainingProgress
                                                                .completed
                                                        }{' '}
                                                        of{' '}
                                                        {
                                                            studentData
                                                                .trainingProgress
                                                                .total
                                                        }{' '}
                                                        modules
                                                    </span>
                                                    <span>
                                                        {
                                                            studentData
                                                                .trainingProgress
                                                                .percent
                                                        }
                                                        %
                                                    </span>
                                                </div>
                                                <AccessibleProgressBar
                                                    now={
                                                        studentData
                                                            .trainingProgress
                                                            .percent
                                                    }
                                                    variant="success"
                                                    label={`Training progress: ${studentData.trainingProgress.percent}%`}
                                                />
                                            </div>
                                        </>
                                    ) : (
                                        <p className="text-muted small mb-0">
                                            No training assigned yet.
                                        </p>
                                    )}
                                </Card.Body>
                            </Card>
                        )}

                        {/* Surveys Card */}
                        {surveysEnabled && (
                            <Card className="mb-4 border-0 shadow-sm">
                                <Card.Header className="bg-transparent border-0 pb-0">
                                    <div className="d-flex justify-content-between align-items-center">
                                        <h2 className="h6 mb-0">Surveys</h2>
                                        <Link to="/surveys" className="small">
                                            View all
                                        </Link>
                                    </div>
                                </Card.Header>
                                <Card.Body>
                                    <p className="text-muted small mb-2">
                                        Help improve peer support by sharing
                                        your feedback through surveys.
                                    </p>
                                    <Button
                                        as={Link}
                                        to="/surveys"
                                        variant="outline-primary"
                                        size="sm"
                                        className="w-100"
                                    >
                                        Check Available Surveys
                                    </Button>
                                </Card.Body>
                            </Card>
                        )}

                        {/* Help */}
                        <Card className="border-0 shadow-sm">
                            <Card.Header className="bg-transparent border-0 pb-0">
                                <h2 className="h6 mb-0">Need help?</h2>
                            </Card.Header>
                            <Card.Body>
                                <p className="text-muted small mb-2">
                                    Visit the <Link to="/about#faq">FAQ</Link>{' '}
                                    or contact your program coordinator.
                                </p>
                                {needsFacultyAccess && (
                                    <p className="text-muted small mb-0">
                                        Faculty or staff? To request peer
                                        support for a course, contact the Peer
                                        Support Hub team at <TeamEmailLink /> to
                                        get faculty access.
                                    </p>
                                )}
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            )}
        </Container>
    );
}

export default Dashboard;
